import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "../ui/sidebar";
import {
  LayoutDashboard,
  FolderOpen,
  CheckSquare,
  Users,
  Package,
  BarChart3,
  Settings,
  Calendar,
  PhilippinePeso,
  Archive,
  Eye,
  Download,
  LogOut,
  Activity,
  CreditCard,
  Sparkles,
} from "lucide-react";
import { User } from "../../types";
import { useSidebar } from "../ui/sidebar";
import Swal from "sweetalert2";
import { useEffect, useMemo, useState, useRef } from "react";
import { CompanyLogo } from "../ui/company-logo";

interface AppSidebarProps {
  currentUser: User;
  onLogout: () => void;
}

export function AppSidebar({ currentUser, onLogout }: AppSidebarProps) {
  const { isMobile, setOpenMobile, state, setOpen } = useSidebar();
  const [activeHash, setActiveHash] = useState("");
  const [isMd, setIsMd] = useState(false);
  const [autoCollapsed, setAutoCollapsed] = useState(false);

  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const alertShownRef = useRef(false);

  // Auto logout configuration: 3 hours of inactivity
  const INACTIVITY_TIME = 3 * 60 * 60 * 1000; // 3 hours (10,800,000 ms)
  const COUNTDOWN_TIME = 180000; // 3 minutes warning before logout
  const COUNTDOWN_SECONDS = COUNTDOWN_TIME / 1000;

  const clearInactivityTimers = () => {
    if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    if (countdownTimeoutRef.current) clearTimeout(countdownTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  };

  const handleActivity = () => {
    if (alertShownRef.current || Swal.isVisible()) return;

    clearInactivityTimers();

    inactivityTimeoutRef.current = setTimeout(() => {
      showInactivityWarning();
    }, INACTIVITY_TIME);
  };

  const showInactivityWarning = () => {
    alertShownRef.current = true;
    clearInactivityTimers();

    Swal.fire({
      title: "Inactivity Warning",
      html: `You will be logged out in <strong><span id="countdown">${COUNTDOWN_SECONDS}</span></strong> seconds due to inactivity.`,
      icon: "warning",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: "Logout Now",
      cancelButtonText: "Stay Logged In",
      didOpen: () => {
        let seconds = COUNTDOWN_SECONDS;
        countdownIntervalRef.current = setInterval(() => {
          seconds--;
          const countdownElement = document.getElementById("countdown");
          if (countdownElement) countdownElement.textContent = seconds.toString();

          if (seconds <= 0) {
            clearInactivityTimers();
            Swal.close();
            performLogout();
          }
        }, 1000);

        countdownTimeoutRef.current = setTimeout(() => {
          clearInactivityTimers();
          Swal.close();
          performLogout();
        }, COUNTDOWN_TIME);
      },
      customClass: {
        container: "swal-container",
        popup: "swal-popup swal-actions-right",
        title: "swal-title",
        htmlContainer: "swal-content",
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
        icon: "swal-icon",
      },
    }).then((result) => {
      clearInactivityTimers();

      if (result.isConfirmed) {
        performLogout();
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        alertShownRef.current = false;
        handleActivity();
      } else if (!Swal.isVisible()) {
        alertShownRef.current = false;
      }
    });
  };

  const performLogout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
    } catch (err) {
      console.error("Logout request failed:", err);
    } finally {
      localStorage.removeItem("authToken");
      onLogout();
      window.location.href = "/";
    }
  };

  useEffect(() => {
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    handleActivity();

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearInactivityTimers();
    };
  }, []);

  useEffect(() => {
    const checkMd = () => {
      const width = window.innerWidth;
      setIsMd(width >= 768 && width < 1024);
    };
    checkMd();
    window.addEventListener("resize", checkMd);
    return () => window.removeEventListener("resize", checkMd);
  }, []);

  useEffect(() => {
    if (isMd) {
      setOpen(false);
      setAutoCollapsed(true);
    } else if (autoCollapsed) {
      setOpen(true);
      setAutoCollapsed(false);
    }
  }, [isMd, setOpen, autoCollapsed]);

  const isCollapsed = state === "collapsed" || isMd;
  const isCollapsedDesktop = isCollapsed && !isMobile;
  const menuLinkClassName = isCollapsedDesktop
    ? "flex items-center justify-center p-5"
    : "flex items-center gap-3 p-5";

  const handleLogoutClick = async () => {
    const result = await Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to log out?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Logout",
      cancelButtonText: "Cancel",
      focusCancel: true,
      customClass: {
        container: "swal-container",
        popup: "swal-popup",
        title: "swal-title",
        htmlContainer: "swal-content",
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
        icon: "swal-icon",
      },
    });

    if (result.isConfirmed) {
      performLogout();
    }
  };

  const navigationGroups = useMemo(() => {
    const homeGroup = {
      title: "Home",
      items: [
        { title: "Dashboard", url: "#dashboard", icon: LayoutDashboard },
      ],
    };

    if (currentUser.role === "admin") {
      return [
        homeGroup,
        {
          title: "Management",
          items: [
            { title: "Projects", url: "#projects", icon: FolderOpen },
            { title: "Archives", url: "#archives", icon: Archive },
            { title: "Tasks", url: "#tasks", icon: CheckSquare },
            { title: "Users", url: "#users", icon: Users },
            { title: "Feature Bank", url: "#feature-bank", icon: Sparkles },
          ],
        },
        {
          title: "Finance",
          items: [
            { title: "Revenue", url: "#revenue", icon: PhilippinePeso },
            { title: "Billing", url: "#billing", icon: CreditCard },
            { title: "Reports", url: "#reports", icon: BarChart3 },
          ],
        },
        {
          title: "System",
          items: [
            { title: "Activity Logs", url: "#activity-logs", icon: Activity },
            { title: "Settings", url: "#settings", icon: Settings },
          ],
        },
      ];
    }

    if (currentUser.role === "supervisor") {
      return [
        homeGroup,
        {
          title: "Management",
          items: [
            { title: "Projects", url: "#projects", icon: FolderOpen },
            { title: "Archives", url: "#archives", icon: Archive },
            { title: "Tasks", url: "#tasks", icon: CheckSquare },
            { title: "Team", url: "#team", icon: Users },
            { title: "Feature Bank", url: "#feature-bank", icon: Sparkles },
          ],
        },
        {
          title: "Insights",
          items: [
            { title: "Reports", url: "#reports", icon: BarChart3 },
          ],
        },
      ];
    }

    if (currentUser.role === "fabricator") {
      return [
        homeGroup,
        {
          title: "Workspace",
          items: [
            { title: "Projects", url: "#projects", icon: FolderOpen },
            { title: "Tasks", url: "#tasks", icon: CheckSquare },
            { title: "Work Log", url: "#worklog", icon: Calendar },
            { title: "Materials", url: "#materials", icon: Package },
            { title: "Feature Bank", url: "#feature-bank", icon: Sparkles },
          ],
        },
        {
          title: "Preferences",
          items: [{ title: "Settings", url: "#settings", icon: Settings }],
        },
      ];
    }

    if (currentUser.role === "client") {
      return [
        homeGroup,
        {
          title: "Project",
          items: [
            { title: "Project Status", url: "#project-status", icon: Eye },
            {
              title: "Documentation",
              url: "#documentation",
              icon: Download,
            },
          ],
        },
      ];
    }

    return [
      homeGroup,
      {
        title: "Management",
        items: [{ title: "Projects", url: "#projects", icon: FolderOpen }],
      },
    ];
  }, [currentUser.role]);

  useEffect(() => {
    const getHash = () =>
      window.location.hash || navigationGroups[0]?.items[0]?.url || "#dashboard";
    const updateHash = () => setActiveHash(getHash());
    updateHash();
    window.addEventListener("hashchange", updateHash);
    return () => window.removeEventListener("hashchange", updateHash);
  }, [navigationGroups]);

  useEffect(() => {
    if (!activeHash) return;
    const raf = window.requestAnimationFrame(() => {
      const content = document.querySelector('[data-sidebar="content"]');
      if (!content) return;
      const activeItem = content.querySelector(
        '[data-active="true"]'
      ) as HTMLElement | null;
      if (!activeItem) return;

      const contentRect = content.getBoundingClientRect();
      const activeRect = activeItem.getBoundingClientRect();
      const isAbove = activeRect.top < contentRect.top;
      const isBelow = activeRect.bottom > contentRect.bottom;

      if (isAbove || isBelow) {
        activeItem.scrollIntoView({ block: "center", inline: "nearest" });
      }
    });

    return () => window.cancelAnimationFrame(raf);
  }, [activeHash, navigationGroups]);

  return (
    <>
      <Sidebar
        className="h-screen min-h-screen border-r-0 text-white [&_[data-sidebar=sidebar]]:border-r [&_[data-sidebar=sidebar]]:border-white/10 [&_[data-sidebar=sidebar]]:bg-sidebar [&_[data-sidebar=sidebar]]:text-sidebar-foreground"
        collapsible="icon"
      >
        <SidebarHeader className="flex h-[73px] shrink-0 items-center border-b border-white/10 px-4 dark:border-white/10 sm:h-[81px]">
          <CompanyLogo
            size={isCollapsedDesktop ? "sm" : "md"}
            showText={false}
            clickable={true}
            variant="dark"
            className={isCollapsedDesktop ? "justify-center" : "max-w-full"}
          />
        </SidebarHeader>

        <SidebarContent className="gap-0 overflow-y-auto overflow-x-hidden bg-transparent no-scrollbar dark:bg-transparent">
          {navigationGroups.map((group) => (
            <SidebarGroup key={group.title} className="px-3 py-4">
              <SidebarGroupLabel className="mb-2 h-auto px-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60 group-data-[collapsible=icon]:hidden">
                {group.title}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1.5 md:group-data-[collapsible=icon]:px-0">
                  {group.items.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={activeHash === item.url}
                        className="app-sidebar-link rounded-lg border border-transparent bg-transparent p-0 text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:border-white/20 transition-all duration-150"
                        tooltip={isCollapsedDesktop ? item.title : undefined}
                      >
                        <a
                          href={item.url}
                          className={menuLinkClassName}
                          onClick={(e) => {
                            if (item.url.startsWith("#")) {
                              e.preventDefault();
                              window.location.hash = item.url;
                            }
                            if (isMobile) setOpenMobile(false);
                          }}
                        >
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center text-current">
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                          </span>
                          <span
                            className={`${isCollapsedDesktop ? "sr-only" : ""} font-normal`}
                          >
                            {item.title}
                          </span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>

        <SidebarFooter className="border-t border-white/10 px-3 py-4 dark:border-white/10">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={handleLogoutClick}
                className="h-auto rounded-lg border border-transparent bg-transparent p-0 text-white/50 hover:bg-white/10 hover:text-white active:bg-white/15 transition-all duration-150"
                tooltip={isCollapsedDesktop ? "Logout" : undefined}
              >
                <div
                  className={
                    isCollapsedDesktop
                      ? "flex items-center justify-center p-3"
                      : "flex items-center gap-3 px-4 py-3"
                  }
                >
                  <span className="flex flex-shrink-0 items-center justify-center text-current -none">
                    <LogOut className="h-4 w-4" />
                  </span>
                  <span className={`${isCollapsedDesktop ? "sr-only" : ""} font-normal`}>
                    Logout
                  </span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    </>
  );
}
